import React from 'react';
import { createRoot } from 'react-dom/client';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Polygon, useMap } from 'react-leaflet';
import Logo from '../../assets/S3Logo.png';
import L from 'leaflet';
import { SubcontractorPanel } from './components/SubcontractorPanel';
import { SettingsModal } from './components/SettingsModal';
import { api, isNumericId, isUuidId } from './api';
import { LoginPage } from './components/LoginPage';
import { FaGear, FaCircleUser, FaPlus, FaTrash } from 'react-icons/fa6';
import { uploadFile, fetchAsDataUrl } from './fileUtils';

// Fix default icon paths for Leaflet when bundling with Vite
// (Otherwise markers may not show because Leaflet looks for images relative to CSS)
// We'll dynamically set the icon URLs using data URIs or copy assets later if needed.
// For now use Leaflet's default icon via unpkg CDN (quick start) - can replace with local assets.
const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface ServiceAreaProps {
  value: L.LatLngExpression[];
  setValue: (updater: (prev: L.LatLngExpression[]) => L.LatLngExpression[]) => void;
  editing: boolean;
  color?: string;
}

// Component to render polygon and optional editable vertex handles
const EditableServiceArea: React.FC<ServiceAreaProps> = ({ value, setValue, editing, color='#ff7e00' }) => {
  const map = useMap();
  const vertexIcon = React.useMemo(() => L.divIcon({
    className: 'service-vertex-icon',
    html: '<div></div>',
    iconSize: [14,14],
    iconAnchor: [7,7]
  }), []);

  // Double-click add vertex only in editing mode
  React.useEffect(() => {
    if(!editing) return;
    const handleDbl = (e: L.LeafletMouseEvent) => {
      const pt: [number, number] = [e.latlng.lat, e.latlng.lng];
      setValue((prev: L.LatLngExpression[]) => {
        if (prev.length < 2) return [...prev, pt];
        // Find segment with minimum distance to insertion point
        let bestIndex = prev.length; // default append
        let bestDist = Number.POSITIVE_INFINITY;
        const toXY = (p: L.LatLngExpression): [number, number] => {
          if (Array.isArray(p)) return [p[0] as number, p[1] as number];
          const ll = p as any; return [ll.lat, ll.lng];
        };
        const sqDistToSeg = (p: [number,number], a: [number,number], b: [number,number]) => {
          // Treat lat/lng as planar for small region approximation
            const [x, y] = p; const [x1, y1] = a; const [x2, y2] = b;
            const dx = x2 - x1; const dy = y2 - y1;
            if (dx === 0 && dy === 0) { const dxp = x - x1; const dyp = y - y1; return dxp*dxp + dyp*dyp; }
            let t = ((x - x1)*dx + (y - y1)*dy) / (dx*dx + dy*dy);
            t = Math.max(0, Math.min(1, t));
            const projx = x1 + t*dx; const projy = y1 + t*dy;
            const ddx = x - projx; const ddy = y - projy; return ddx*ddx + ddy*ddy;
        };
        const pts = prev.map(p => toXY(p));
        for (let i = 0; i < pts.length; i++) {
          const a = pts[i];
            const b = pts[(i + 1) % pts.length]; // closed polygon
            const d = sqDistToSeg(pt, a, b);
            if (d < bestDist) { bestDist = d; bestIndex = i + 1; }
        }
        const newArr = [...prev];
        newArr.splice(bestIndex, 0, pt);
        return newArr;
      });
    };
    map.on('dblclick', handleDbl);
    return () => { map.off('dblclick', handleDbl); };
  }, [map, editing, setValue]);

  return (
    <>
      {value.length > 2 && (
        <Polygon positions={value} pathOptions={{ color, weight: 2, fillOpacity: 0.15 }} />
      )}
      {editing && value.map((p, idx) => (
        <Marker
          key={idx}
          position={p}
          draggable
          icon={vertexIcon}
          eventHandlers={{
            dragend: ev => {
              const latlng = (ev.target as L.Marker).getLatLng();
              setValue((prev: L.LatLngExpression[]) => prev.map((pt: L.LatLngExpression, i: number) => i === idx ? [latlng.lat, latlng.lng] : pt));
            },
            contextmenu: () => { // right-click remove point
              setValue((prev: L.LatLngExpression[]) => prev.filter((_, i: number) => i !== idx));
            }
          }}
        />
      ))}
    </>
  );
};

type RelItem = { id?: number; name: string };
type VocabItem = { id: number; name: string };
type CWCompany = {
  id: number;
  name: string;
  identifier?: string;
  phoneNumber?: string;
  website?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  cwLat?: number | null;
  cwLng?: number | null;
};

interface SubcontractorData {
  id: string;
  name: string;
  cw_id?: string | number | null;
  position: [number, number];
  serviceArea: L.LatLngExpression[];
  verified?: boolean;
  rating: number;
  status?: 'Active' | 'Inactive' | 'Pending';
  basic: any; // keep loose for now (existing panel expects shape)
  certifications: RelItem[];
  services: RelItem[];
  tickets: { id: string; title: string; date: string }[];
  purchaseOrders?: { id: string; number: string; date?: string; status?: string }[];
  cwCompanyIdentifier?: string;
  attachments: { id?: string | number; fileName: string; expirationDate?: string | null; storageKey?: string | null }[];
  avatar?: string | null; // data URL
  cwNotes?: string | null; // aggregated CW notes (read-only)
  cwNotesItems?: { id?: string | number; date?: string; by?: string; text: string }[];
  primaryContact?: { id?: number; name?: string; title?: string; email?: string | null; phone?: string | null } | null;
  notes?: string; // Local editable notes
  pinColor?: string; // Color for map pin and service area (defaults to #ff7e00)
}

const App: React.FC = () => {
  // Auth state and data
  const [user, setUser] = React.useState<{ email: string } | null>(null);
  const [subcontractors, setSubcontractors] = React.useState<SubcontractorData[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>('');
  const [editingArea, setEditingArea] = React.useState<boolean>(false);
  const [certVocab, setCertVocab] = React.useState<VocabItem[]>([]);
  const [svcVocab, setSvcVocab] = React.useState<VocabItem[]>([]);
  const [cwCompanies, setCwCompanies] = React.useState<CWCompany[]>([]);
  const [cwLastFetchedAt, setCwLastFetchedAt] = React.useState<number | null>(null);
  const [cwError, setCwError] = React.useState<string | null>(null);
  type CwGeo = { id: number; name: string; lat: number; lng: number; displayName: string };
  const [cwGeocoded, setCwGeocoded] = React.useState<CwGeo[]>([]);
  const cwSyncRunning = React.useRef<boolean>(false);

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = React.useState<boolean>(false);

  // Notes auto-save state
  const [notesSaving, setNotesSaving] = React.useState<boolean>(false);
  const notesTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // CW loading state
  const [cwLoading, setCwLoading] = React.useState<boolean>(false);

  // Compute centroid of a polygon (lat,lng) using planar approximation (sufficient for small regions)
  const polygonCentroid = React.useCallback((pts: L.LatLngExpression[]): [number, number] => {
    if (!pts || pts.length < 3) return pts && pts.length ? (Array.isArray(pts[0]) ? (pts[0] as [number, number]) : [(pts[0] as any).lat, (pts[0] as any).lng]) : [40, -83];
    // Convert to array of [lat,lng]
    const p = pts.map(pt => Array.isArray(pt) ? [pt[0] as number, pt[1] as number] : [(pt as any).lat, (pt as any).lng]);
    let area = 0;
    let cx = 0; let cy = 0;
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
      const x0 = p[j][1]; // use lng as x
      const y0 = p[j][0]; // lat as y
      const x1 = p[i][1];
      const y1 = p[i][0];
      const f = x0 * y1 - x1 * y0;
      area += f;
      cx += (x0 + x1) * f;
      cy += (y0 + y1) * f;
    }
    if (area === 0) {
      // fallback to average
      const avgLat = p.reduce((s,a)=>s+a[0],0)/p.length;
      const avgLng = p.reduce((s,a)=>s+a[1],0)/p.length;
      return [avgLat, avgLng];
    }
    area *= 0.5;
    cx /= (6 * area);
    cy /= (6 * area);
    return [cy, cx]; // cy is latitude, cx is longitude
  }, []);

  // Map API record -> UI model (handles centroid and GeoJSON polygon)
  const mapRecordToSub = React.useCallback((r: any): SubcontractorData => {
    // Helpers to normalize CSV-like nulls and types
    const isNullishStr = (v: any) => v === null || v === undefined || v === '' || v === 'NULL' || v === 'null' || v === 'None';
    const cleanStr = (v: any): string => isNullishStr(v) ? '' : String(v);
    const cleanUrlOrNull = (v: any): string | null => isNullishStr(v) ? null : String(v);
    const toBool = (v: any): boolean => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'string') return ['true','t','1','yes','y'].includes(v.trim().toLowerCase());
      return Boolean(v);
    };
  const id = String(r.id);
    const name = cleanStr(r.name) || `Sub ${id}`;
  const cwId = r.cw_id ?? r.cwId ?? r.cwCompanyId ?? null;
    // Build service area from GeoJSON if present (convert [lng,lat] -> [lat,lng])
    let serviceArea: L.LatLngExpression[] = [];
    const gj = r.service_area_geojson || r.service_area || r.serviceAreaGeojson || r.serviceArea;
    if (gj && typeof gj === 'object' && gj.type === 'Polygon' && Array.isArray(gj.coordinates) && Array.isArray(gj.coordinates[0])) {
      const ring = gj.coordinates[0] as Array<[number, number]>; // [lng, lat]
      serviceArea = ring
        .filter(pt => Array.isArray(pt) && pt.length >= 2)
        .map(([lng, lat]) => {
          const latNum = typeof lat === 'string' ? parseFloat(lat) : Number(lat);
          const lngNum = typeof lng === 'string' ? parseFloat(lng) : Number(lng);
          return [Number.isFinite(latNum) ? latNum : (lat as any), Number.isFinite(lngNum) ? lngNum : (lng as any)] as [number, number];
        });
      // If last equals first, we can drop the closing duplicate for editing convenience
      if (serviceArea.length > 1) {
        const a = serviceArea[0] as [number, number];
        const b = serviceArea[serviceArea.length - 1] as [number, number];
        if (a[0] === b[0] && a[1] === b[1]) serviceArea = serviceArea.slice(0, -1);
      }
    }
    // Derive position
    let position: [number, number];
    if (serviceArea.length > 2) {
      position = polygonCentroid(serviceArea);
    } else {
      // Accept multiple centroid key variants from API
      const rawLat = r.centroid_lat ?? r.centroidLat ?? r.centroidLatitude;
      const rawLng = r.centroid_lng ?? r.centroid_long ?? r.centroidLng ?? r.centroidLong ?? r.centroidLongitude;
      const latOk = !isNullishStr(rawLat);
      const lngOk = !isNullishStr(rawLng);
      if (latOk && lngOk) {
        const latNum = typeof rawLat === 'string' ? parseFloat(rawLat) : Number(rawLat);
        const lngNum = typeof rawLng === 'string' ? parseFloat(rawLng) : Number(rawLng);
        if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
          position = [latNum, lngNum];
        } else {
          position = [40, -83];
        }
      } else {
        position = [40, -83];
      }
    }
    // Normalize rating (0-5)
    const ratingNumRaw = typeof r.rating === 'string' ? parseFloat(r.rating) : Number(r.rating);
    const ratingNum = Number.isFinite(ratingNumRaw) ? Math.max(0, Math.min(5, ratingNumRaw)) : 0;
    // Normalize status
    const statusVal = isNullishStr(r.status) ? 'Pending' : String(r.status);
    // Normalize related lists if provided by API
    const mapRel = (arr: any): RelItem[] => {
      if (!Array.isArray(arr)) return [];
      return arr.map((x: any) => {
        if (!x) return null as any;
        if (typeof x === 'string') return { name: x } as RelItem;
        if (typeof x === 'object') {
          const name = (x.name ?? x.title ?? x.label ?? '').toString();
          const idVal = x.id ?? x.certification_id ?? x.service_id;
          const id = idVal !== undefined && idVal !== null && idVal !== '' ? Number(idVal) : undefined;
          return { id: Number.isFinite(id as number) ? (id as number) : undefined, name } as RelItem;
        }
        return null as any;
      }).filter(Boolean);
    };
    return {
      id,
      name,
  cw_id: cwId,
      position,
      serviceArea,
      verified: toBool(r.verified),
      rating: ratingNum,
      status: statusVal as any,
      avatar: cleanUrlOrNull(r.avatar_url),
      basic: {
        phone: cleanStr(r.phone),
        email: cleanStr(r.email),
        website: cleanStr(r.website),
        address1: cleanStr(r.address1),
        address2: cleanStr(r.address2),
        city: cleanStr(r.city),
        state: cleanStr(r.state),
        zip: cleanStr(r.zip),
        travelDistance: cleanStr(r.travel_distance),
        tmRate: cleanStr(r.tm_rate),
        tmRateLastUpdated: r.tm_rate_last_updated || undefined,
        uin: cleanStr(r.uin),
        ein: cleanStr(r.ein)
  },
  certifications: mapRel(r.certifications),
  services: mapRel(r.services),
  tickets: [], attachments: [], cwNotesItems: [],
  notes: cleanStr(r.notes)
    };
  }, [polygonCentroid]);

  const loadSubcontractors = React.useCallback(async () => {
    try {
      const list = await api.listSubcontractors({ pageSize: 100 });
      const rows = Array.isArray(list?.data) ? list.data : [];
      // Determine which items lack usable geometry
      const isNullish = (v: any) => v === null || v === undefined || v === '' || v === 'NULL' || v === 'null';
      const hasGeom = (r: any) => {
        const gj = r?.service_area_geojson || r?.service_area || r?.serviceAreaGeojson || r?.serviceArea;
        const lat = r?.centroid_lat ?? r?.centroidLat ?? r?.centroidLatitude;
        const lng = r?.centroid_lng ?? r?.centroid_long ?? r?.centroidLng ?? r?.centroidLong ?? r?.centroidLongitude;
        const gjOk = gj && typeof gj === 'object' && gj.type === 'Polygon' && Array.isArray(gj.coordinates) && Array.isArray(gj.coordinates[0]) && gj.coordinates[0].length > 2;
        const latOk = !isNullish(lat) && Number.isFinite(typeof lat === 'string' ? parseFloat(lat) : Number(lat));
        const lngOk = !isNullish(lng) && Number.isFinite(typeof lng === 'string' ? parseFloat(lng) : Number(lng));
        return gjOk || (latOk && lngOk);
      };
      const missing = rows.filter(r => !hasGeom(r));
      let detailsById = new Map<string, any>();
      if (missing.length > 0) {
        // Fetch details in small batches to avoid flooding the API
        const ids = missing.map(r => String(r.id));
        const batchSize = 8;
        for (let i = 0; i < ids.length; i += batchSize) {
          const slice = ids.slice(i, i + batchSize);
          const res = await Promise.allSettled(slice.map(id => api.getSubcontractor(id)));
          res.forEach((p, idx) => {
            if (p.status === 'fulfilled' && p.value) {
              detailsById.set(slice[idx], p.value);
            }
          });
        }
      }
      const merged = rows.map(r => detailsById.get(String(r.id)) || r);
      setSubcontractors(prev => {
        const mapped = merged.map(mapRecordToSub);
        return mapped.length ? mapped : prev;
      });
    } catch (err) {
      console.warn('API listSubcontractors failed:', err);
    }
  }, [mapRecordToSub]);

  React.useEffect(() => {
    // Load user via first-party proxy to ensure cookies are sent
    (async () => {
      try {
        const { ok, status, text } = await (window as any).api.request('GET', '/api/user');
        if (!ok) return;
        const raw = JSON.parse(text);
        const container = raw.user && typeof raw.user === 'object' ? raw.user : raw;
        const email = container.email || (Array.isArray(container.emails) ? container.emails[0] : undefined);
        if (email) {
          setUser({ email });
        }
      } catch { /* ignore */ }
    })();
  }, []);

  React.useEffect(() => {
    if (!user) return;
    // Always fetch subcontractors once when user is available
    loadSubcontractors();
  }, [user, loadSubcontractors]);

  // Load vocabularies after login
  React.useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [c, s] = await Promise.allSettled([api.listCerts(), api.listServices()]);
        if (c.status === 'fulfilled' && Array.isArray(c.value)) {
          setCertVocab(c.value.map((x: any) => ({ id: Number(x.id), name: String(x.name) })));
        }
        if (s.status === 'fulfilled' && Array.isArray(s.value)) {
          setSvcVocab(s.value.map((x: any) => ({ id: Number(x.id), name: String(x.name) })));
        }
      } catch {/* ignore */}
    })();
  }, [user]);

  // Fetch CW Subcontractor companies after login (best-effort)
  const fetchCWCompanies = React.useCallback(async () => {
    console.log('[CW] fetchCWCompanies called');
    try {
      console.log('[CW] Making request to /api/cw/companies');
      const res = await api.cw.listCompanies(100, 50);
      console.log('[CW] Response:', res);
      if (res && res.ok && Array.isArray(res.data)) {
        console.log('[CW] Success, companies:', res.data.length);
        setCwCompanies(res.data);
        setCwError(null);
      } else {
        console.warn('[CW] Fetch failed:', res?.error);
        setCwCompanies([]);
        setCwError(res?.error || 'Failed to fetch ConnectWise companies');
      }
    } catch (e) {
      console.error('[CW] Fetch error:', e);
      setCwCompanies([]);
      setCwError(String(e));
    }
  }, []);

  // Fetch CW companies when user logs in
  React.useEffect(() => {
    if (!user) return;
    fetchCWCompanies();
  }, [user, fetchCWCompanies]);
  const logout = async () => {
    await fetch('/auth/logout', { 
      method: 'POST', 
      credentials: 'include'
    });
    setUser(null);
  };
    // Persisted refs controlling map auto-fit behavior
    const mapHasFitOnceRef = React.useRef<boolean>(false);
    const mapUserMovedRef = React.useRef<boolean>(false);

  // states already declared above

  // Filters
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [ratingFilter, setRatingFilter] = React.useState<string>('');
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const filteredContractors = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
    const ratingThreshold = ratingFilter ? parseInt(ratingFilter[0], 10) : 0;
    const hay = (c: SubcontractorData) => {
      const b = c.basic || ({} as any);
      const certs = (c.certifications || []).map(x => x?.name || '').join(' ');
      const svcs = (c.services || []).map(x => x?.name || '').join(' ');
      const text: string = [
        c.name,
        c.status || '',
        c.verified ? 'verified' : 'unverified',
        b.phone, b.email, b.website, b.address1, b.address2, b.city, b.state, b.zip,
        certs,
        svcs,
        (c.tickets||[]).map(t => `${t.id} ${t.title}`).join(' '),
        (c.attachments||[]).map(a => a.fileName).join(' ')
      ].filter(Boolean).join(' ').toLowerCase();
      return text;
    };
    return subcontractors.filter(c => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (ratingThreshold && !(c.rating >= ratingThreshold)) return false;
      if (tokens.length) {
        const text = hay(c);
        for (const t of tokens) { if (!text.includes(t)) return false; }
      }
      return true;
    });
  }, [subcontractors, statusFilter, ratingFilter, searchQuery]);

  // Initialize selection only once when empty; do not auto-rotate on subsequent list changes
  React.useEffect(() => {
    if (!filteredContractors.length) return;
    if (!selectedId) {
      setSelectedId(filteredContractors[0].id);
    }
  }, [filteredContractors, selectedId]);

  const selected = subcontractors.find(s => s.id === selectedId);
  // Cache to avoid re-fetching attachments for the same subcontractor repeatedly
  const attachmentsLoadedRef = React.useRef<Set<string>>(new Set());

  // Create the selected subcontractor as a company in ConnectWise when not already linked (no cw_id)
  const createInConnectWise = React.useCallback(async () => {
    const sc = subcontractors.find(s => s.id === selectedId);
    if (!sc) return;
    // Guard: if already linked to CW, do nothing
    if (sc.cw_id !== null && sc.cw_id !== undefined && sc.cw_id !== '') {
      alert('This subcontractor is already linked to ConnectWise.');
      return;
    }
    
    // Validate that we have coordinates
    const lat = sc.position[0];
    const lng = sc.position[1];
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 40 && lng === -83) {
      alert('Cannot create in ConnectWise: No valid coordinates. Please ensure the address is set and geocoded.');
      return;
    }
    
    try {
      // Include latLong in payload for custom field
      const latLongValue = `${lat.toFixed(7)},${lng.toFixed(7)}`;
      const payload = { 
        name: sc.name, 
        basic: sc.basic,
        latLong: latLongValue  // Will be set as custom field in CW
      };
      console.log('[CW] Creating company:', payload);
      const res = await api.cw.upsertCompanyForSub(payload);
      console.log('[CW] Response:', res);
      if (res && res.ok && res.data && (res.data.id !== null && res.data.id !== undefined)) {
        const cwId = res.data.id;
        console.log('[CW] Success, company ID:', cwId);
        // Update UI state immediately
        setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, cw_id: cwId } : x));
        // Best-effort persist to backend so future sessions know it's linked
        try {
          if (isNumericId(sc.id) || isUuidId(sc.id)) {
            await api.updateSubcontractor(sc.id, { cw_id: cwId } as any);
          }
        } catch {/* ignore if backend does not yet accept cw_id */}
        alert(`Successfully created in ConnectWise!\nCompany ID: ${cwId}\nCoordinates: ${latLongValue}`);
      } else {
        console.error('[CW] Create failed:', {
          error: res?.error,
          detail: res?.detail,
          fullResponse: res
        });
        alert(`Failed to create in ConnectWise:\n${res?.error || 'Unknown error'}\n\nDetails: ${JSON.stringify(res?.detail || {}, null, 2)}`);
      }
    } catch (e) {
      console.error('[CW] Exception:', e);
      alert(`Error creating in ConnectWise: ${e}`);
    }
  }, [selectedId, subcontractors]);

  // Fit map to markers once when data first appears. Persist flags across rerenders via refs passed from parent.
  const FitOnFirstData: React.FC<{ points: Array<[number, number]>; hasFitOnceRef: React.MutableRefObject<boolean>; userMovedRef: React.MutableRefObject<boolean> } > = ({ points, hasFitOnceRef, userMovedRef }) => {
    const map = useMap();

    // If user interacts (drag/zoom), stop any auto-fitting thereafter
    React.useEffect(() => {
      const onStart = () => { userMovedRef.current = true; };
      map.on('dragstart', onStart);
      map.on('zoomstart', onStart);
      return () => {
        map.off('dragstart', onStart);
        map.off('zoomstart', onStart);
      };
    }, [map]);

    // Only fit once when data first appears, and not after user interaction
    React.useEffect(() => {
      if (hasFitOnceRef.current || userMovedRef.current) return;
      if (!points || points.length === 0) return;
      const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
      if (bounds.isValid()) {
        try {
          map.fitBounds(bounds.pad(0.15));
          hasFitOnceRef.current = true;
        } catch {}
      }
    }, [map, points]);
    return null;
  };

  // Load attachments once per selected subcontractor (if API provides them)
  React.useEffect(() => {
    (async () => {
      if (!selectedId || !(isNumericId(selectedId) || isUuidId(selectedId))) return;
      if (attachmentsLoadedRef.current.has(String(selectedId))) return;
      try {
        const list = await api.listAttachments(selectedId);
        if (Array.isArray(list)) {
          setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
            ...sc,
            attachments: list.map((a: any) => ({
              id: a.id,
              fileName: a.file_name || a.name || String(a.id || 'file'),
              expirationDate: a.expiration_date ?? a.expires_on ?? null,
              storageKey: a.storage_key ?? a.key ?? null,
              category: a.category || a.type || null,
              downloadUrl: a.download_url || null
            }))
          } : sc));
          attachmentsLoadedRef.current.add(String(selectedId));
        }
      } catch {/* ignore if endpoint not available */}
    })();
  }, [selectedId]);

  // Track CW notes pagination per selected company (client-side control)
  const [cwNotesPageByCompany, setCwNotesPageByCompany] = React.useState<Record<string, number>>({});

  // Track which company IDs have had their initial CW page loaded to avoid duplicate fetches (e.g., React StrictMode)
  const cwInitialLoadedRef = React.useRef<Record<string, boolean>>({});

  // When a CW-linked subcontractor is selected, pull first page (5 items) for tickets and notes from CW if not already loaded
  React.useEffect(() => {
    (async () => {
      const sc = subcontractors.find(s => s.id === selectedId);
      const cwId = sc?.cw_id;
      console.log('[CW Data Fetch] Selected subcontractor:', sc?.name, 'cw_id:', cwId);
      if (!sc || cwId === null || cwId === undefined || cwId === '') {
        console.log('[CW Data Fetch] Skipping - no ConnectWise link (cw_id is missing)');
        return;
      }
      const companyId = Number(cwId);
      if (!Number.isFinite(companyId)) {
        console.log('[CW Data Fetch] Skipping - invalid company ID:', cwId);
        return;
      }
      const key = String(companyId);
      if (cwInitialLoadedRef.current[key]) {
        console.log('[CW Data Fetch] Skipping - already loaded for company:', companyId);
        return;
      }
      cwInitialLoadedRef.current[key] = true;
      console.log('[CW Data Fetch] Starting fetch for company:', companyId);
      
      // Check if data already exists (from auto-import)
      const hasTickets = sc.tickets && sc.tickets.length > 0;
      const hasNotes = sc.cwNotesItems && sc.cwNotesItems.length > 0;
      const hasContact = sc.primaryContact !== undefined;
      const hasPurchaseOrders = sc.purchaseOrders && sc.purchaseOrders.length > 0;
      const hasCompanyIdentifier = sc.cwCompanyIdentifier !== undefined;
      
      console.log('[CW Data Fetch] Data status:', { hasTickets, hasNotes, hasContact, hasPurchaseOrders, hasCompanyIdentifier });
      
      // Skip fetching if data already exists
      if (hasTickets && hasNotes && hasContact && hasPurchaseOrders && hasCompanyIdentifier) {
        console.log('[CW Data Fetch] Skipping - all data already loaded');
        return;
      }
      
      // Try to get company identifier from cwCompanies list (already fetched)
      const cwCompany = cwCompanies.find(c => c.id === companyId);
      console.log('[CW Data Fetch] Found CW company in list:', cwCompany?.name, 'identifier:', cwCompany?.identifier);
      
      setCwLoading(true); // Start loading
      try {
        const promises: Promise<any>[] = [];
        if (!hasTickets) promises.push(api.cw.listCompanyTickets(companyId, undefined, 5, 1));
        if (!hasNotes) promises.push(api.cw.listCompanyNotes(companyId, undefined, 5, 1));
        if (!hasContact) promises.push(api.cw.getCompanyPrimaryContact(companyId));
        if (!hasPurchaseOrders) promises.push(api.cw.listCompanyPurchaseOrders(companyId, undefined, 5, 1));
        // Don't fetch company details - use identifier from cwCompanies list instead
        
        console.log('[CW Data Fetch] Fetching', promises.length, 'resources');
        const results = await Promise.allSettled(promises);
        let idx = 0;
        
        // Tickets
        if (!hasTickets) {
          const ticketsRes = results[idx++];
          if (ticketsRes.status === 'fulfilled' && ticketsRes.value && ticketsRes.value.ok) {
            const rows: any[] = Array.isArray(ticketsRes.value.data) ? ticketsRes.value.data : [];
            const mapped = rows.slice(0, 5).map(t => ({
              id: String(t.id ?? ''),
              title: String(t.summary || t.title || ''),
              date: (t.closedDate || t.lastUpdated || t.dateEntered || '') as string
            }));
            setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, tickets: mapped } : x));
          }
        }
        
        // Notes -> store only current page items; do not aggregate full blob
        if (!hasNotes) {
          const notesRes = results[idx++];
          if (notesRes.status === 'fulfilled' && notesRes.value && notesRes.value.ok) {
            const rows: any[] = Array.isArray(notesRes.value.data) ? notesRes.value.data : [];
            const items = rows.slice(0, 5).map(n => ({ id: n.id, date: n.dateEntered || n.lastUpdated || '', by: n.enteredBy || '', text: (n.text || '').toString() }));
            setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, cwNotes: null, cwNotesItems: items } : x));
            setCwNotesPageByCompany(prev => ({ ...prev, [String(companyId)]: 1 }));
          }
        }
        
        // Primary contact
        if (!hasContact) {
          const contactRes = results[idx++];
          if (contactRes.status === 'fulfilled' && contactRes.value && contactRes.value.ok) {
            const data = contactRes.value.data || null;
            setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, primaryContact: data } : x));
          }
        }
        
        // Purchase Orders
        if (!hasPurchaseOrders) {
          const poRes = results[idx++];
          if (poRes.status === 'fulfilled' && poRes.value && poRes.value.ok) {
            const rows: any[] = Array.isArray(poRes.value.data) ? poRes.value.data : [];
            const mapped = rows.slice(0, 5).map(po => {
              // Handle nested objects from ConnectWise API
              const statusValue = typeof po.status === 'object' ? (po.status?.name || po.status?.id || '') : (po.status || '');
              const dateValue = typeof po.orderDate === 'object' ? '' : (po.orderDate || po.poDate || '');
              return {
                id: String(po.id ?? ''),
                number: String(po.poNumber || po.number || ''),
                date: String(dateValue),
                status: String(statusValue)
              };
            });
            console.log('[CW Data Fetch] Loaded', mapped.length, 'purchase orders:', mapped);
            setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, purchaseOrders: mapped } : x));
          } else {
            console.warn('[CW Data Fetch] Purchase orders failed:', poRes.status === 'rejected' ? poRes.reason : poRes.value);
          }
        }
        
        // Set company identifier from cwCompanies list (or fallback)
        if (!hasCompanyIdentifier && cwCompany) {
          const identifier = cwCompany.identifier || 'mys3tech';
          console.log('[CW Data Fetch] Setting company identifier:', identifier);
          setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, cwCompanyIdentifier: identifier } : x));
        }
      } catch (err) {
        console.warn('[CW] Failed to fetch CW data:', err);
      } finally {
        setCwLoading(false); // End loading
      }
    })();
  }, [selectedId, subcontractors, cwCompanies]);

  // Handlers to change CW pages (server-side paginated 5/page)
  const handleCwNotesPageChange = React.useCallback(async (page: number) => {
    const sc = subcontractors.find(s => s.id === selectedId);
    const cwId = sc?.cw_id;
    if (!sc || cwId === null || cwId === undefined || cwId === '') return;
    const companyId = Number(cwId);
    if (!Number.isFinite(companyId)) return;
    try {
      const res = await api.cw.listCompanyNotes(companyId, undefined, 5, page);
      if (res && res.ok) {
        const rows: any[] = Array.isArray(res.data) ? res.data : [];
        const items = rows.slice(0, 5).map(n => ({ id: n.id, date: n.dateEntered || n.lastUpdated || '', by: n.enteredBy || '', text: (n.text || '').toString() }));
        setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, cwNotesItems: items } : x));
        setCwNotesPageByCompany(prev => ({ ...prev, [String(companyId)]: page }));
      }
    } catch {/* ignore */}
  }, [selectedId, subcontractors]);

  const handleCwTicketsPageChange = React.useCallback(async (page: number) => {
    const sc = subcontractors.find(s => s.id === selectedId);
    const cwId = sc?.cw_id;
    if (!sc || cwId === null || cwId === undefined || cwId === '') return;
    const companyId = Number(cwId);
    if (!Number.isFinite(companyId)) return;
    try {
      const res = await api.cw.listCompanyTickets(companyId, undefined, 5, page);
      if (res && res.ok) {
        const rows: any[] = Array.isArray(res.data) ? res.data : [];
        const mapped = rows.slice(0, 5).map((t: any) => ({ id: String(t.id ?? ''), title: String(t.summary || t.title || ''), date: (t.closedDate || t.lastUpdated || t.dateEntered || '') as string }));
        setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, tickets: mapped } : x));
      }
    } catch {/* ignore */}
  }, [selectedId, subcontractors]);

  const handleCwPurchaseOrdersPageChange = React.useCallback(async (page: number) => {
    const sc = subcontractors.find(s => s.id === selectedId);
    const cwId = sc?.cw_id;
    if (!sc || cwId === null || cwId === undefined || cwId === '') return;
    const companyId = Number(cwId);
    if (!Number.isFinite(companyId)) return;
    try {
      const res = await api.cw.listCompanyPurchaseOrders(companyId, undefined, 5, page);
      if (res && res.ok) {
        const rows: any[] = Array.isArray(res.data) ? res.data : [];
        const mapped = rows.slice(0, 5).map((po: any) => {
          // Handle nested objects from ConnectWise API
          const statusValue = typeof po.status === 'object' ? (po.status?.name || po.status?.id || '') : (po.status || '');
          const dateValue = typeof po.orderDate === 'object' ? '' : (po.orderDate || po.poDate || '');
          return {
            id: String(po.id ?? ''),
            number: String(po.poNumber || po.number || ''),
            date: String(dateValue),
            status: String(statusValue)
          };
        });
        setSubcontractors(prev => prev.map(x => x.id === sc.id ? { ...x, purchaseOrders: mapped } : x));
      }
    } catch {/* ignore */}
  }, [selectedId, subcontractors]);

  // Documents removed

  const updateSelectedServiceArea = (updater: (prev: L.LatLngExpression[]) => L.LatLngExpression[]) => {
    setSubcontractors(prev => prev.map(sc => {
      if (sc.id !== selectedId) return sc;
      const newArea = updater(sc.serviceArea);
      const newPos = newArea.length > 2 ? polygonCentroid(newArea) : sc.position;
      return { ...sc, serviceArea: newArea, position: newPos };
    }));
  };

  const generateDefaultQuad = (center: [number, number]): L.LatLngExpression[] => {
    const [lat, lng] = center;
    const dLat = 0.15; // approx ~10-15 miles; tweak as needed
    const dLng = 0.25; // depends on latitude, rough box
    return [
      [lat + dLat, lng - dLng],
      [lat + dLat, lng + dLng],
      [lat - dLat, lng + dLng],
      [lat - dLat, lng - dLng]
    ];
  };

  const handleToggleEdit = async () => {
    if (!selected) return;
    setEditingArea(prevEdit => {
      if (!prevEdit) { // turning on
        if (selected.serviceArea.length === 0) {
          // create a square around current position
            setSubcontractors(prev => prev.map(sc => sc.id === selected.id ? { ...sc, serviceArea: generateDefaultQuad(sc.position), position: sc.position } : sc));
        }
      }
      return !prevEdit;
    });
    // When toggling OFF edit mode (saving), persist polygon
    try {
  const now = subcontractors.find(s => s.id === selectedId);
  // Save when we were in editing mode before this toggle
  if (now && editingArea && (isNumericId(now.id) || isUuidId(now.id)) && now.serviceArea.length > 2) {
        const coords = now.serviceArea.map(pt => Array.isArray(pt) ? [ (pt as [number,number])[1], (pt as [number,number])[0] ] : [ (pt as any).lng, (pt as any).lat ]);
        if (coords.length && (coords[0][0] !== coords[coords.length-1][0] || coords[0][1] !== coords[coords.length-1][1])) {
          coords.push([coords[0][0], coords[0][1]]);
        }
        const geojson = { type: 'Polygon', coordinates: [coords] } as any;
        const [lat, lng] = polygonCentroid(now.serviceArea);
        await api.updateSubcontractor(now.id, { service_area_geojson: geojson, centroid_lat: lat, centroid_lng: lng });
      }
    } catch (e) {
      console.warn('Failed to save service area:', e);
    }
  };

  const updateSelectedAvatar = async (dataUrl: string | null) => {
    if (!selectedId) return;
    // Update UI immediately
    setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, avatar: dataUrl } : sc));
    // If cleared, just persist null
    if (dataUrl === null) {
      try { if ((isNumericId(selectedId) || isUuidId(selectedId))) await api.updateSubcontractor(selectedId, { avatar_url: null }); } catch {}
      return;
    }
  // Try uploading to /files/upload for persistence; do not store base64 dataUrl in backend
    try {
      // Require a real server ID so backend can create a storage_key automatically
      if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
        console.warn('Avatar not uploaded: subcontractor must be saved first.');
        return;
      }
      const m = /^data:([^;]+);base64,/.exec(dataUrl || '') || [];
      const contentType = m[1] || 'image/png';
      const ext = contentType.includes('jpeg') ? 'jpg' : contentType.split('/')[1] || 'png';
      const fileName = `avatar.${ext}`;
      let avatarUrlForServer: string | null = null;
      let uploadedKey: string | null = null; // capture storage key to identify any auto-created attachment
      
      const up = await uploadFile({
        url: '/files/upload',  // Use relative URL to go through proxy/Traefik
        method: 'POST',
        fields: { subcontractorId: selectedId, type: 'avatars', disposition: 'inline' },
        fileFieldName: 'file',
        fileName,
        contentType,
        dataUrl
      });
      
      if (up?.ok) {
        try {
          const obj = JSON.parse(up.text || '{}');
          // Backend returns { storage_key, public_url, ... }
          const key = (obj.storage_key || obj.key) as string | undefined;
          const pub = obj.public_url as string | undefined;
          if (key) uploadedKey = String(key);
          if (pub) {
            avatarUrlForServer = String(pub);
          } else if (key) {
            avatarUrlForServer = `/files?key=${encodeURIComponent(key)}`;
          }
        } catch {}
      }
      
      // Persist to subcontractor only if we have a server URL; never send dataUrl in JSON
      if (avatarUrlForServer && (isNumericId(selectedId) || isUuidId(selectedId))) {
        await api.updateSubcontractor(selectedId, { avatar_url: avatarUrlForServer });
      }
      // Best-effort cleanup: if the upload inadvertently created an attachments row, remove it.
      // We match by returned storage_key (preferred) or by the known avatar filename.
      try {
        const list: any[] = await api.listAttachments(selectedId);
        if (Array.isArray(list)) {
          const toDelete = list.filter((a: any) => {
            const name = a.file_name || a.name || '';
            const key = a.storage_key || a.key || '';
            const category = a.type || a.category || '';
            // Prefer exact storage_key match; otherwise match by filename and category hints
            if (uploadedKey && key && String(key) === uploadedKey) return true;
            const nameMatch = String(name).toLowerCase() === fileName.toLowerCase();
            const looksLikeAvatar = String(category).toLowerCase().includes('avatar') || String(name).toLowerCase().startsWith('avatar.');
            return nameMatch || looksLikeAvatar;
          });
          for (const a of toDelete) {
            if (a && (a.id !== undefined && a.id !== null)) {
              try { await api.deleteAttachment(selectedId, a.id); } catch {}
            }
          }
        }
      } catch { /* ignore cleanup failures */ }
      if (avatarUrlForServer) {
        // Try to fetch as data URL via authenticated fetch so <img> can display it
        try {
          const r = await fetchAsDataUrl(avatarUrlForServer);
          if (r?.ok && r.dataUrl) {
            setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, avatar: r.dataUrl as string } : sc));
          } else {
            // Fallback to using the direct URL
            setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, avatar: avatarUrlForServer } : sc));
          }
        } catch {
          setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, avatar: avatarUrlForServer } : sc));
        }
      }
    } catch (e) {
      console.warn('Avatar upload failed; keeping local preview only:', e);
      // Do not persist dataUrl to backend; avoids hitting JSON body-size limits
    }
  };

  // Convert any server-side avatar URLs into data URLs using authenticated fetch so images display
  React.useEffect(() => {
    const targets = subcontractors.filter(sc => sc.avatar && typeof sc.avatar === 'string' && !String(sc.avatar).startsWith('data:') && /^https?:\/\//i.test(String(sc.avatar)));
    if (!targets.length) return;
    let cancelled = false;
    (async () => {
      for (const sc of targets) {
        const url = String(sc.avatar);
        try {
          const r = await (window as any).api.fetchDataUrl(url);
          if (!cancelled && r?.ok && r.dataUrl) {
            setSubcontractors(prev => prev.map(x => (x.id === sc.id && x.avatar === url) ? { ...x, avatar: r.dataUrl as string } : x));
          }
        } catch {/* ignore */}
      }
    })();
    return () => { cancelled = true; };
  }, [subcontractors]);

  async function sha256Hex(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    const bytes = new Uint8Array(hash);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  const handleRemoveAttachment = async (attachmentId: string | number) => {
    if (!selectedId) return;
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) return;
    
    try {
      // Call API to delete the attachment
      await api.deleteAttachment(selectedId, attachmentId);
      
      // Update local state
      setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
        ...sc,
        attachments: sc.attachments.filter(a => String(a.id) !== String(attachmentId))
      } : sc));
    } catch (e) {
      console.warn('Failed to delete attachment:', e);
      // Try to refresh from server to sync state
      try {
        const list = await api.listAttachments(selectedId);
        if (Array.isArray(list)) {
          setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
            ...sc,
            attachments: list.map((a: any) => ({
              id: a.id,
              fileName: a.file_name || a.name || String(a.id || 'file'),
              expirationDate: a.expiration_date ?? a.expires_on ?? null,
              storageKey: a.storage_key ?? (a.key || null),
              category: a.category || a.type || null,
              downloadUrl: a.download_url || null
            }))
          } : sc));
        }
      } catch {/* ignore */}
    }
  };

  const handleAddAttachments = async (files: File[]) => {
    console.log('[Attachments] handleAddAttachments called with', files.length, 'file(s)');
    console.trace('[Attachments] Call stack trace');
    if (!selectedId || files.length === 0) return;
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
      console.warn('[Attachments] Not uploaded: subcontractor must be saved first.');
      return;
    }
    console.log('[Attachments] Starting upload for', files.length, 'file(s), subcontractor:', selectedId);
    
    // Convert to data URLs and upload via /files/upload, then upsert metadata with returned key
    for (const f of files) {
      try {
        // Extract category if marked by component (e.g., COI, WC)
        const category = (f as any).__category || 'attachments';
        console.log('[Attachments] Processing file:', f.name, 'category:', category);
        
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onerror = () => reject(new Error('read failed'));
          reader.onload = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(f);
        });
        // Upload to files service with server-side metadata creation
        let uploadedKey: string | null = null;
        let publicUrl: string | null = null;
        
        console.log('[Attachments] Uploading to storage service...');
        const up = await uploadFile({
          url: '/files/upload',  // Use relative URL to go through proxy/Traefik
          method: 'POST',
          fields: { subcontractorId: selectedId, type: category, disposition: category.startsWith('insurance_') ? 'inline' : 'attachment' },
          fileFieldName: 'file',
          fileName: f.name,
          contentType: f.type || 'application/octet-stream',
          dataUrl
        });
        
        if (up?.ok) {
          try {
            const obj = JSON.parse(up.text || '{}');
            uploadedKey = obj.storage_key || null;
            publicUrl = obj.public_url || null;
            console.log('[Attachments] Upload successful, storage_key:', uploadedKey);
          } catch (e) { 
            console.warn('[Attachments] Failed to parse upload response:', e);
          }
        } else {
          console.warn('[Attachments] Upload failed with status:', up?.status);
        }
        
        if (!uploadedKey) {
          console.warn('[Attachments] No storage_key received; skipping metadata save');
          continue; // Skip to next file
        }
        
        console.log('[Attachments] File uploaded successfully, storage_key:', uploadedKey);
        
        // First, check what attachments exist before upsert
        try {
          const beforeList = await api.listAttachments(selectedId);
          console.log('[Attachments] Attachments before upsert:', beforeList.map((a: any) => ({ 
            id: a.id, 
            file_name: a.file_name, 
            category: a.category 
          })));
        } catch {}
        
        // Update the attachment record created by upload service to add category
        // The backend uses ON CONFLICT (subcontractor_id, file_name) to update existing record
        const payload = {
          file_name: f.name,
          storage_key: uploadedKey,
          content_type: f.type || 'application/octet-stream',
          byte_size: f.size,
          category: category
        };
        console.log('[Attachments] Updating attachment with category:', payload);
        
        try {
          const result = await api.upsertAttachment(selectedId, payload);
          console.log('[Attachments] Attachment updated successfully:', result);
        } catch (err) {
          console.error('[Attachments] Failed to update attachment:', err);
          continue; // Skip to next file
        }
        
        // Delay a bit before refreshing to allow server processing
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Refresh from server to get the newly uploaded file with its metadata
        console.log('[Attachments] Refreshing attachment list from backend...');
        try {
          const list = await api.listAttachments(selectedId);
          console.log('[Attachments] Received', Array.isArray(list) ? list.length : 0, 'attachment(s) from backend:', list);
          if (Array.isArray(list)) {
            setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
              ...sc,
              attachments: list.map((a: any) => ({
                id: a.id,
                fileName: a.file_name || a.name || String(a.id || 'file'),
                expirationDate: a.expiration_date ?? a.expires_on ?? null,
                storageKey: a.storage_key ?? a.key ?? null,
                category: a.category || a.type || null,
                downloadUrl: a.download_url || null
              }))
            } : sc));
          }
        } catch (err) {
          console.error('[Attachments] Failed to refresh attachments:', err);
        }
      } catch (e) {
        console.warn('Failed to process attachment:', e);
      }
    }
  };

  // Set or clear an attachment's expiration date on the server, then refresh
  const setAttachmentExpiry = async (attachmentId: string | number, isoDate?: string) => {
    if (!selectedId) return;
    const sc = subcontractors.find(s => s.id === selectedId);
    if (!sc) return;
    const att = sc.attachments.find(a => String(a.id) === String(attachmentId));
    if (!att) return;
    try {
      if ((isNumericId(selectedId) || isUuidId(selectedId)) && att.storageKey) {
        // Use upsert with storage_key + file_name to update expiration_date
        await api.upsertAttachment(selectedId, {
          file_name: att.fileName,
          storage_key: att.storageKey,
          expiration_date: isoDate || undefined
        });
      }
    } catch (e) { console.warn('Failed to set attachment expiration:', e); }
    // Update UI optimistically and refresh from server
    setSubcontractors(prev => prev.map(s => s.id === selectedId ? {
      ...s,
      attachments: s.attachments.map(a => String(a.id) === String(attachmentId) ? { ...a, expirationDate: isoDate || null } : a)
    } : s));
    try {
      const list = await api.listAttachments(selectedId);
      if (Array.isArray(list)) {
        setSubcontractors(prev => prev.map(s => s.id === selectedId ? {
          ...s,
          attachments: list.map((a: any) => ({
            id: a.id,
            fileName: a.file_name || a.name || String(a.id || 'file'),
            expirationDate: a.expiration_date ?? a.expires_on ?? null,
            storageKey: a.storage_key ?? a.key ?? null,
            category: a.category || a.type || null,
            downloadUrl: a.download_url || null
          }))
        } : s));
      }
    } catch {/* ignore */}
  };

  const addSubcontractor = async () => {
    setEditingArea(false);
    
    // Prompt for address to geocode the new contractor's location
    const address = prompt('Enter the address for the new subcontractor (e.g., "123 Main St, Milwaukee, WI 53202"):');
    if (!address) return; // User cancelled
    
    try {
      // Parse address components
      const parts = address.split(',').map(s => s.trim());
      const address1 = parts[0] || '';
      const city = parts[1] || '';
      const stateZip = parts[2] || '';
      const stateZipMatch = stateZip.match(/^([A-Z]{2})\s*(\d{5})?$/);
      const state = stateZipMatch?.[1] || '';
      const zip = stateZipMatch?.[2] || '';
      
      // Geocode the address
      const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
      const geoData = await geoRes.json();
      
      let lat = 40; // Default fallback
      let lng = -83;
      
      if (Array.isArray(geoData) && geoData.length > 0) {
        lat = parseFloat(geoData[0].lat);
        lng = parseFloat(geoData[0].lon);
      }
      
      // Create with geocoded coordinates
      const created = await api.createSubcontractor({ 
        name: 'New Subcontractor', 
        status: 'Pending',
        address1,
        city,
        state,
        zip,
        centroid_lat: lat,
        centroid_lng: lng
      });
      const id = String(created.id);
      // Reload using the enriched loader (pulls details for missing geometry)
      await loadSubcontractors();
      setSelectedId(id);
    } catch (e) {
      console.warn('Failed to create subcontractor:', e);
      alert(`Error creating subcontractor: ${e}`);
    }
  };

  const deleteSubcontractor = async () => {
    if (!selectedId) return;
    const sc = subcontractors.find(s => s.id === selectedId);
    const cwLinked = sc && sc.cw_id !== null && sc.cw_id !== undefined && sc.cw_id !== '';
    let prompt = 'Delete this subcontractor? This cannot be undone.';
    // Removed: ConnectWise company deletion - only delete local record
    const ok = window.confirm(prompt);
    if (!ok) return;
    setEditingArea(false);
    try {
      // Only delete local subcontractor, never delete from ConnectWise
      if (isNumericId(selectedId) || isUuidId(selectedId)) {
        await api.deleteSubcontractor(selectedId);
      }
    } catch (e) {
      console.warn('Failed to delete on server:', e);
    }
    setSubcontractors(prev => {
      const filtered = prev.filter(sc => sc.id !== selectedId);
      if (filtered.length === 0) {
        // Always keep at least one blank entry
        const id = `sc-${Date.now()}`;
        const blank: SubcontractorData = {
          id,
          name: 'New Subcontractor',
          position: [40,-83],
          serviceArea: [],
          verified: false,
          rating: 0,
          avatar: null,
          basic: { phone:'', email:'', website:'', address1:'', address2:'', city:'', state:'', travelDistance:'', tmRate:'', uin:'', ein:'' },
          certifications: [],
          services: [],
          tickets: [],
          attachments: []
        };
        setSelectedId(id);
        return [blank];
      }
      // choose first remaining
      setSelectedId(filtered[0].id);
      return filtered;
    });
  };

  // Helpers to resolve or create vocab entries
  const findCertByName = (name: string) => certVocab.find(v => v.name.toLowerCase() === name.trim().toLowerCase());
  const findSvcByName = (name: string) => svcVocab.find(v => v.name.toLowerCase() === name.trim().toLowerCase());

  const addCertification = async (name: string) => {
    if (!selectedId) return;
    // Ensure this subcontractor exists on the server before linking
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
      window.alert('Please save this subcontractor before adding certifications.');
      return;
    }
    const target = subcontractors.find(s => s.id === selectedId);
    if (!target) return;
    let vocab = findCertByName(name);
    try {
      if (!vocab) {
        const created = await api.addCert(name.trim());
        vocab = { id: Number(created.id), name: String(created.name || name) };
        setCertVocab(prev => [...prev, vocab!]);
      }
      if (vocab) {
        await api.linkCertification(selectedId, vocab.id);
        setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
          ...sc,
          certifications: sc.certifications.some(x => x.id === vocab!.id || x.name.toLowerCase() === vocab!.name.toLowerCase())
            ? sc.certifications
            : [...sc.certifications, { id: vocab!.id, name: vocab!.name }]
        } : sc));
        // Refresh from server to ensure persistence and canonical IDs
        try {
          const fresh = await api.getSubcontractor(selectedId);
          const mapped = mapRecordToSub(fresh);
          setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, certifications: mapped.certifications } : sc));
        } catch (e) { console.warn('Failed to refresh certifications from server:', e); }
      }
    } catch (e) { console.warn('Failed to add certification:', e); }
  };

  const removeCertification = async (name: string) => {
    if (!selectedId) return;
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
      window.alert('Please save this subcontractor before removing certifications.');
      return;
    }
    const vocab = findCertByName(name);
    if (!vocab) return;
    try {
      await api.unlinkCertification(selectedId, vocab.id);
    } catch (e) { console.warn('Failed to unlink certification:', e); }
    setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
      ...sc,
      certifications: sc.certifications.filter(x => (x.id ?? -1) !== vocab.id && x.name.toLowerCase() !== name.toLowerCase())
    } : sc));
    // Refresh from server to ensure persistence
    try {
      const fresh = await api.getSubcontractor(selectedId);
      const mapped = mapRecordToSub(fresh);
      setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, certifications: mapped.certifications } : sc));
    } catch (e) { console.warn('Failed to refresh certifications from server:', e); }
  };

  const addService = async (name: string) => {
    if (!selectedId) return;
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
      window.alert('Please save this subcontractor before adding services.');
      return;
    }
    const target = subcontractors.find(s => s.id === selectedId);
    if (!target) return;
    let vocab = findSvcByName(name);
    try {
      if (!vocab) {
        const created = await api.addService(name.trim());
        vocab = { id: Number(created.id), name: String(created.name || name) };
        setSvcVocab(prev => [...prev, vocab!]);
      }
      if (vocab) {
        await api.linkService(selectedId, vocab.id);
        setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
          ...sc,
          services: sc.services.some(x => x.id === vocab!.id || x.name.toLowerCase() === vocab!.name.toLowerCase())
            ? sc.services
            : [...sc.services, { id: vocab!.id, name: vocab!.name }]
        } : sc));
        // Refresh from server to ensure persistence and canonical IDs
        try {
          const fresh = await api.getSubcontractor(selectedId);
          const mapped = mapRecordToSub(fresh);
          setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, services: mapped.services } : sc));
        } catch (e) { console.warn('Failed to refresh services from server:', e); }
      }
    } catch (e) { console.warn('Failed to add service:', e); }
  };

  const removeService = async (name: string) => {
    if (!selectedId) return;
    if (!(isNumericId(selectedId) || isUuidId(selectedId))) {
      window.alert('Please save this subcontractor before removing services.');
      return;
    }
    const vocab = findSvcByName(name);
    if (!vocab) return;
    try {
      await api.unlinkService(selectedId, vocab.id);
    } catch (e) { console.warn('Failed to unlink service:', e); }
  setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? {
      ...sc,
      services: sc.services.filter(x => (x.id ?? -1) !== vocab.id && x.name.toLowerCase() !== name.toLowerCase())
  } : sc));
    // Refresh from server to ensure persistence
    try {
      const fresh = await api.getSubcontractor(selectedId);
      const mapped = mapRecordToSub(fresh);
      setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, services: mapped.services } : sc));
    } catch (e) { console.warn('Failed to refresh services from server:', e); }
  };

  // Debounced notes handler: save 2 seconds after typing stops
  const handleNotesChange = React.useCallback((notes: string) => {
    if (!selectedId) return;
    
    // Update local state immediately
    setSubcontractors(prev => prev.map(sc => 
      sc.id === selectedId ? { ...sc, notes } : sc
    ));
    
    // Clear existing timeout
    if (notesTimeoutRef.current) {
      clearTimeout(notesTimeoutRef.current);
    }
    
    // Set new timeout for save
    setNotesSaving(true);
    notesTimeoutRef.current = setTimeout(async () => {
      try {
        if (isNumericId(selectedId) || isUuidId(selectedId)) {
          await api.updateSubcontractor(selectedId, { notes } as any);
          // Keep saving indicator for a moment to show "Saved"
          setTimeout(() => setNotesSaving(false), 1500);
        }
      } catch (err) {
        console.warn('Failed to save notes:', err);
        setNotesSaving(false);
      }
    }, 2000);
  }, [selectedId]);

  const handleStatusChange = React.useCallback(async (newStatus: 'Active' | 'Inactive' | 'Pending') => {
    if (!selectedId) return;
    // Update UI immediately (optimistic update)
    setSubcontractors(prev => prev.map(sc => 
      sc.id === selectedId ? { ...sc, status: newStatus } : sc
    ));
    // Persist to backend
    try {
      if (isNumericId(selectedId) || isUuidId(selectedId)) {
        await api.updateSubcontractor(selectedId, { status: newStatus } as any);
      }
    } catch (err) {
      console.warn('Failed to update status:', err);
    }
  }, [selectedId]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (notesTimeoutRef.current) {
        clearTimeout(notesTimeoutRef.current);
      }
    };
  }, []);

  // Custom avatar-based div icon
  const makeAvatarIcon = React.useCallback((avatar: string | null | undefined, isSelected: boolean, name?: string, pinColor?: string) => {
    const color = pinColor || '#ff7e00'; // Default orange
    const size = isSelected ? 44 : 36;
    const innerSize = size - 6;
    const safeSrc = avatar ? String(avatar).replace(/"/g, '&quot;') : null;
    
    // Generate initials from name (take first letter of first 2 words)
    const initials = name 
      ? name.trim().split(/\s+/).slice(0, 2).map(word => word[0]?.toUpperCase() || '').join('')
      : 'SC';
    
    const imgPart = safeSrc ? `<img src="${safeSrc}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;"/>` : `<div style="width:100%;height:100%;border-radius:50%;background:linear-gradient(135deg,#444,#222);display:flex;align-items:center;justify-content:center;font-size:10px;color:#777;font-family:system-ui;">${initials}</div>`;
    
    // Calculate lighter color for selected state
    const lighterColor = isSelected ? color.replace(/^#/, '').match(/.{2}/g)?.map(hex => {
      const val = Math.min(255, parseInt(hex, 16) + 80);
      return val.toString(16).padStart(2, '0');
    }).join('') : null;
    const selectedBorderColor = lighterColor ? `#${lighterColor}` : '#ffd28a';
    
    return L.divIcon({
      className: 'pin-avatar' + (isSelected ? ' selected' : ''),
      html: `<div class='pin-avatar-inner' style='width:${innerSize}px;height:${innerSize}px;border:2px solid ${isSelected ? selectedBorderColor : color};border-radius:50%;overflow:hidden;box-shadow:${isSelected ? `0 0 0 2px rgba(${parseInt(color.slice(1, 3), 16)},${parseInt(color.slice(3, 5), 16)},${parseInt(color.slice(5, 7), 16)},0.4),0 2px 8px rgba(0,0,0,0.8)` : '0 2px 6px rgba(0,0,0,0.7)'};'>${imgPart}</div>`,
      iconSize: [size, size],
      iconAnchor: [size/2, size/2],
      popupAnchor: [0, -size/2]
    });
  }, []);

  // Simple badge icon for CW companies
  // Geocode CW companies to coordinates (best-effort). Caches in localStorage by address string.
  React.useEffect(() => {
    if (!cwCompanies || cwCompanies.length === 0) { setCwGeocoded([]); return; }
    let cancelled = false;
    const cacheKey = 'cwGeoCache.v1';
    const cache: Record<string, { lat: number; lng: number; t: number }> = (() => {
      try { return JSON.parse(localStorage.getItem(cacheKey) || '{}') || {}; } catch { return {}; }
    })();
    const norm = (s?: string | null) => (s || '').toString().trim();
    const addrOf = (c: CWCompany) => {
      const country = norm(c.country) || 'USA';
      const parts = [norm(c.address1), norm(c.city), norm(c.state), norm(c.zip), country].filter(Boolean);
      return parts.join(', ');
    };
    const list: Array<{ c: CWCompany; q: string }> = cwCompanies
      .map(c => ({ c, q: addrOf(c) }))
      // Relax filters: try if we have any non-empty address string
      .filter(x => x.q.length > 0);
    // Build result array from cache first
    const seed: CwGeo[] = [];
    const missing: Array<{ c: CWCompany; q: string }> = [];
    for (const it of list) {
      const hit = cache[it.q];
      if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) {
        seed.push({ id: it.c.id, name: it.c.name, lat: hit.lat, lng: hit.lng, displayName: it.q });
      } else {
        missing.push(it);
      }
    }
    setCwGeocoded(seed);
    if (missing.length === 0) return;
    // Sequential geocode to be nice to the provider
    (async () => {
      for (let i = 0; i < missing.length; i++) {
        if (cancelled) break;
        const { c, q } = missing[i];
        try {
          const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}`;
          const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
          if (!res.ok) { await new Promise(r => setTimeout(r, 600)); continue; }
          const arr = await res.json();
          const first = Array.isArray(arr) && arr.length ? arr[0] : null;
          if (first) {
            const lat = parseFloat(first.lat);
            const lng = parseFloat(first.lon);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              cache[q] = { lat, lng, t: Date.now() };
              try { localStorage.setItem(cacheKey, JSON.stringify(cache)); } catch { /* ignore quota */ }
              if (!cancelled) setCwGeocoded(prev => {
                const next = prev.filter(x => x.id !== c.id);
                next.push({ id: c.id, name: c.name, lat, lng, displayName: q });
                return next;
              });
            }
          }
        } catch { /* ignore */ }
        // Be polite: small delay between requests
        await new Promise(r => setTimeout(r, 600));
      }
    })();
    return () => { cancelled = true; };
  }, [cwCompanies]);



  // Geocode local subcontractors that have missing coordinates
  const geocodeLocalSubcontractors = React.useCallback(async () => {
    try {
      // Get all subcontractors with addresses but no coordinates
      const needsGeocode = subcontractors.filter(sc => {
        const hasAddress = sc.basic?.address1 || sc.basic?.city;
        const missingCoords = !sc.position || sc.position[0] === null || sc.position[1] === null;
        return hasAddress && missingCoords;
      });

      if (needsGeocode.length === 0) {
        alert('All subcontractors with addresses already have coordinates.');
        return;
      }

      const confirmed = confirm(`Geocode ${needsGeocode.length} subcontractor(s) with missing coordinates?`);
      if (!confirmed) return;

      let successCount = 0;
      for (const sc of needsGeocode) {
        try {
          const addr = [
            sc.basic?.address1,
            sc.basic?.city,
            sc.basic?.state,
            sc.basic?.zip
          ].filter(Boolean).join(', ');

          if (!addr) continue;

          // Call geocoding API
          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}`);
          const data = await res.json();

          if (Array.isArray(data) && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              await api.updateSubcontractor(sc.id, {
                centroid_lat: lat,
                centroid_lng: lng
              } as any);
              successCount++;
            }
          }

          // Rate limit: 1 request per second for Nominatim
          await new Promise(r => setTimeout(r, 1000));
        } catch (err) {
          console.warn(`Failed to geocode ${sc.name}:`, err);
        }
      }

      alert(`Geocoded ${successCount} of ${needsGeocode.length} subcontractor(s).`);
      await loadSubcontractors();
    } catch (err) {
      console.error('Geocoding error:', err);
      alert('Failed to geocode subcontractors.');
    }
  }, [subcontractors, loadSubcontractors]);

  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  return (
    <>
      <header className="app-header" style={{display:'flex',alignItems:'center',gap:12}}>
        <img src={Logo} alt="S3 Technologies" style={{height:40, width:'auto'}} />
  <div className="header-spacer" />
  <button className="icon-btn" title="Add Subcontractor" aria-label="Add Subcontractor" onClick={addSubcontractor}><FaPlus /></button>
  <button className="icon-btn" title="Delete Selected" aria-label="Delete Subcontractor" onClick={deleteSubcontractor} disabled={!selectedId}><FaTrash /></button>
        <div style={{fontSize:12,color:'#888',marginRight:8}}>{user.email}</div>
        <button className="icon-btn" title="Settings" aria-label="Settings" onClick={() => setSettingsOpen(true)}><FaGear/></button>
        <button className="icon-btn user" title="Logout" aria-label="Logout" onClick={logout}><FaCircleUser/></button>
      </header>
      <main>
  <div className="content" style={{ width: '100%', height: 'calc(100vh - 110px)' }}>
          <MapContainer center={[40.0, -82.98]} zoom={6} style={{ height: '100%', width: '100%' }} doubleClickZoom={false}>
            <TileLayer
              url="https://services.arcgisonline.com/arcgis/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}.png"
            />
            <FitOnFirstData points={filteredContractors.map(sc => sc.position)} hasFitOnceRef={mapHasFitOnceRef} userMovedRef={mapUserMovedRef} />
            {/* Non-selected polygons (filtered) */}
            {filteredContractors.filter(sc => sc.id !== selectedId && sc.serviceArea.length > 2).map(sc => (
              <Polygon
                key={sc.id}
                positions={sc.serviceArea}
                pathOptions={{ color:'#666', weight:1, dashArray:'4 4', fillOpacity:0.05 }}
                eventHandlers={{ click: () => { setSelectedId(sc.id); setEditingArea(false); } }}
                className="service-area-poly selectable"
              />
            ))}
            {/* Markers for filtered subcontractors - NOT draggable (position determined by address geocoding) */}
            {filteredContractors.map(sc => {
              const isSelected = sc.id === selectedId;
              return (
                <Marker
                  key={sc.id}
                  position={sc.position as L.LatLngExpression}
                  icon={makeAvatarIcon(sc.avatar, isSelected, sc.name, sc.pinColor)}
                  draggable={false}
                  eventHandlers={{
                    click: () => { setSelectedId(sc.id); setEditingArea(false); }
                  }}
                />
              );
            })}
            {selected && (
              <EditableServiceArea
                value={selected.serviceArea}
                setValue={updateSelectedServiceArea}
                editing={editingArea}
                color={selected.pinColor || "#ff7e00"}
              />
            )}
          </MapContainer>
          <div className="map-filters-floating">
            <div className="filters-shell">
              {cwError && (
                <div style={{ color:'#f66', fontSize:12, marginRight:8 }} title={cwError}>
                  CW error: {cwError}
                </div>
              )}
              <label className="f-item">
                <span>Status</span>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="">Any</option>
                  <option>Active</option>
                  <option>Inactive</option>
                  <option>Pending</option>
                </select>
              </label>
              <label className="f-item">
                <span>Rating</span>
                <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}>
                  <option value="">Any</option>
                  <option>5★</option>
                  <option>4★+</option>
                  <option>3★+</option>
                </select>
              </label>
              <input
                className="flt-search"
                placeholder="Search subcontractors, services, certifications..."
                aria-label="Global search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <button className="f-reset" onClick={() => { setStatusFilter(''); setRatingFilter(''); setSearchQuery(''); }} title="Reset Filters">Reset</button>
              <div className="f-count" style={{marginLeft:8, fontSize:12, color:'#aaa'}}>{filteredContractors.length} shown of {subcontractors.length}</div>
            </div>
          </div>
          {selected && (
            <div className="map-tools">
              <button onClick={handleToggleEdit} disabled={!selected}>
                {editingArea ? 'Save Area' : (selected?.serviceArea?.length ? 'Edit Service Area' : 'Add Service Area')}
              </button>
              {/* Show CW create button only when not linked (no cw_id) */}
              {(!selected.cw_id && selected.cw_id !== 0) && (
                <button onClick={createInConnectWise} title="Create this contractor as a company in ConnectWise">
                  Create in ConnectWise
                </button>
              )}
              {editingArea && (
                <div className="map-edit-hint">
                  <strong>Editing Service Area</strong><br />
                  Double-click: add vertex (nearest edge)<br />
                  Drag: move vertex & shape<br />
                  Right-click vertex: remove
                </div>
              )}
            </div>
          )}
        </div>
  {selected && (
          <SubcontractorPanel
            key={selected.id}
            name={selected.name}
            verified={selected.verified}
            rating={selected.rating}
            status={selected.status}
            basic={selected.basic}
            certifications={selected.certifications}
            services={selected.services}
            allCertifications={certVocab}
            allServices={svcVocab}
            tickets={selected.tickets}
            cwTicketsHasMore={selected.tickets.length === 4}
            onCwTicketsPageChange={handleCwTicketsPageChange}
            purchaseOrders={selected.purchaseOrders || []}
            cwPurchaseOrdersHasMore={(selected.purchaseOrders || []).length === 4}
            onCwPurchaseOrdersPageChange={handleCwPurchaseOrdersPageChange}
            cwCompanyIdentifier={selected.cwCompanyIdentifier || 'mys3tech'}
            attachments={selected.attachments}
            // CW Documents removed
            cwNotes={selected.cwNotes || null}
            cwNotesItems={selected.cwNotesItems || []}
            cwNotesHasMore={(selected.cwNotesItems || []).length === 4}
            onCwNotesPageChange={handleCwNotesPageChange}
            primaryContact={selected.primaryContact || null}
            avatar={selected.avatar || undefined}
            onAvatarChange={updateSelectedAvatar}
            onAddAttachments={handleAddAttachments}
            onRemoveAttachment={handleRemoveAttachment}
            onSetAttachmentExpiry={setAttachmentExpiry}
      onVerifiedChange={async (value) => {
              if (!selectedId) return;
              setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, verified: value } : sc));
              try {
        if (isNumericId(selectedId) || isUuidId(selectedId)) {
                  await api.updateSubcontractor(selectedId, { verified: value });
                }
              } catch (e) {
                console.warn('Failed to update verified:', e);
              }
            }}
            onNameChange={async (newName) => {
              if (!selectedId) return;
              setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, name: newName } : sc));
              try {
        if (isNumericId(selectedId) || isUuidId(selectedId)) {
                  await api.updateSubcontractor(selectedId, { name: newName });
                }
              } catch (e) {
                console.warn('Failed to update name:', e);
              }
            }}
            onBasicChange={async (field, value) => {
              if (!selectedId) return;
              setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, basic: { ...sc.basic, [field]: value } } : sc));
              const fieldMap: Record<string, string> = {
                phone: 'phone', email: 'email', website: 'website', address1: 'address1', address2: 'address2', city: 'city', state: 'state', zip: 'zip', travelDistance: 'travel_distance', tmRate: 'tm_rate', uin: 'uin', ein: 'ein'
              };
              const apiKey = fieldMap[String(field)] || String(field);
              try {
        if (isNumericId(selectedId) || isUuidId(selectedId)) {
                  await api.updateSubcontractor(selectedId, { [apiKey]: value } as any);
                }
              } catch (e) {
                console.warn('Failed to update basic field:', e);
              }
              
              // Auto-geocode when address fields change to reposition marker
              const addressFields = ['address1', 'city', 'state', 'zip'];
              if (addressFields.includes(String(field))) {
                const sc = subcontractors.find(s => s.id === selectedId);
                if (!sc) return;
                
                // Build address string from updated basic info
                const updatedBasic = { ...sc.basic, [field]: value };
                const addr = [
                  updatedBasic?.address1,
                  updatedBasic?.city,
                  updatedBasic?.state,
                  updatedBasic?.zip
                ].filter(Boolean).join(', ');
                
                if (addr) {
                  try {
                    // Geocode the address
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}`);
                    const data = await res.json();
                    
                    if (Array.isArray(data) && data.length > 0) {
                      const lat = parseFloat(data[0].lat);
                      const lng = parseFloat(data[0].lon);
                      
                      if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        // Update position in UI immediately
                        setSubcontractors(prev => prev.map(s => s.id === selectedId ? { ...s, position: [lat, lng] } : s));
                        
                        // Persist to database
                        if (isNumericId(selectedId) || isUuidId(selectedId)) {
                          await api.updateSubcontractor(selectedId, {
                            centroid_lat: lat,
                            centroid_lng: lng
                          } as any);
                        }
                        console.log(`[Geocode] Updated position for ${sc.name} to [${lat}, ${lng}]`);
                      }
                    }
                  } catch (err) {
                    console.warn(`Failed to geocode address for ${sc.name}:`, err);
                  }
                }
              }
            }}
            onRatingChange={async (newRating) => {
              if (!selectedId) return;
              setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, rating: newRating } : sc));
              try {
        if (isNumericId(selectedId) || isUuidId(selectedId)) {
                  await api.updateSubcontractor(selectedId, { rating: newRating });
                }
              } catch (e) {
                console.warn('Failed to update rating:', e);
              }
            }}
            onStatusChange={handleStatusChange}
            onAddCertification={addCertification}
            onRemoveCertification={removeCertification}
            onAddService={addService}
            onRemoveService={removeService}
            notes={selected.notes}
            notesSaving={notesSaving}
            onNotesChange={handleNotesChange}
            cwLoading={cwLoading}
            pinColor={selected.pinColor}
            onPinColorChange={async (newColor) => {
              if (!selectedId) return;
              setSubcontractors(prev => prev.map(sc => sc.id === selectedId ? { ...sc, pinColor: newColor } : sc));
              try {
                if (isNumericId(selectedId) || isUuidId(selectedId)) {
                  await api.updateSubcontractor(selectedId, { pin_color: newColor } as any);
                }
              } catch (e) {
                console.warn('Failed to update pin color:', e);
              }
            }}
          />
        )}
      </main>
      <footer>© {new Date().getFullYear()} S3 Technologies</footer>
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

createRoot(document.getElementById('root') as HTMLElement).render(<App />);
